import React, {FC, useCallback, useMemo, useState} from 'react';
import FiltersPopup from '@vms/FiltersPopup';
import ActionsBar from '@vms/ActionsBar';
import FiltersButton from '@vms/FiltersButton';
import {useFiltersContext} from '@core/FiltersContext';
import {getTimeframeEnumValue, ID, LINE_TABS, LineTab, Timeframe, UnitType} from '@models';
import {useFoodFlavorsReport} from '@api/useFoodFlavorsReport';
import {useFoodFlavorsHistoricTrendPptReport} from '@api/useFoodFlavorsHistoricTrendPptReport';
import {iFoodFlavorHistoricTrendItem, useFoodFlavorsHistoricTrend} from '@api/useFoodFlavorsHistoricTrend';
import {components} from '@api/api';
import {useUserContext} from '@core/UserContext';
import Footer from '@vms/Footer';
import {useFoodsFlavorsFilters} from '@core/selectors/useFoodsFlavorsFilters';
import useAnalyzePerformanceMarkup from "@vms/AnalyzePerformanceMarkup";
import FoodsFlavorsTimeframesTable from "@pages/FoodsFlavors/TimeframesTable";
import FoodsFlavorsTotalTable from "@pages/FoodsFlavors/TotalTable";
import LineTabs from "@components/LineTabs";
import AnalyzePerformanceChartWrapper from "@vms/AnalyzePerformanceChartWrapper";
import AnalyzePerformanceHeaderBar from "@vms/AnalyzePerformanceHeaderBar";
import AnalyzePerformanceFooterAction from "@vms/AnalyzePerformanceFooterAction";
import AnalyzePerformanceWrapper from "@vms/AnalyzePerformanceWrapper";
import AnalyzePerformanceNavHeader from "@vms/AnalyzePerformanceNavHeader";
import AnalyzePerformanceChart from "@vms/AnalyzePerformanceChart";
import {equalInLC} from "@core/utils/string";

const FoodsFlavorsContent: FC = () => {
    const {fullAccess} = useUserContext();

    const {
        options: { mainMetrics },
    } = useFiltersContext();
    const unbrandedMetric = mainMetrics.find(i => equalInLC(i.title, 'unbranded pi'))!;

    const filters = useFoodsFlavorsFilters();

    const [selectedUnit, setSelectedUnit] = useState<UnitType>(UnitType.Count);
    const [selectedPeriod, setSelectedPeriod] = useState<Timeframe>(Timeframe.Season);
    const [selectedMetricId, setSelectedMetricId] = useState<ID>(unbrandedMetric.id);
    const [activeTab, setActiveTab] = useState<LineTab>(LineTab.HistoricTrend);

    const payload = useMemo((): components['schemas']['FoodsFlavorsFilterModel'] => {
        const indexType = selectedUnit === UnitType.Count ? null : selectedMetricId;
        return {
            ...filters,
            indexType: indexType as components['schemas']['FoodsFlavorsFilterModel']['indexType'],
            periodType: getTimeframeEnumValue(selectedPeriod),
        };
    }, [filters, selectedPeriod, selectedUnit, selectedMetricId]);

    const {data, isLoading} = useFoodFlavorsHistoricTrend(
        payload,
        selectedUnit === UnitType.Count ? false : !fullAccess
    );

    const changeActiveTab = useCallback((tab: LineTab) => setActiveTab(tab), [setActiveTab])

    const markupPayload = useMemo(() => ({
        data,
        selectedPeriod,
    }), [data, selectedPeriod])
    const {
        chartItems,
        chartData,
        addChartItem,
        removeChartItem,
        selectedCategories,
        setSelectedCategories
    } = useAnalyzePerformanceMarkup<iFoodFlavorHistoricTrendItem>(markupPayload)

    const report = useFoodFlavorsReport(selectedCategories);

    const pptReport = useFoodFlavorsHistoricTrendPptReport(
        selectedPeriod,
        selectedUnit === UnitType.Count ? null : selectedMetricId,
        chartItems.map(i => i.id),
    );

    const noAccess = useMemo(
        () => selectedUnit === UnitType.Pct && !fullAccess,
        [selectedUnit, fullAccess]
    );

    return (
        <>
            <AnalyzePerformanceWrapper>
                <AnalyzePerformanceNavHeader
                    title={'Foods & Flavors'}
                    selectedUnit={selectedUnit}
                    setSelectedUnit={setSelectedUnit}
                />
                {
                    selectedUnit === UnitType.Pct && (
                        <LineTabs
                            className="FoodsFlavors__line-tabs"
                            data={LINE_TABS}
                            activeTabId={activeTab}
                            onItemClick={changeActiveTab}
                            testId={"blockLineTabs"}
                        />
                    )
                }
                {
                    (selectedUnit === UnitType.Count || activeTab === LineTab.HistoricTrend) && (
                        <AnalyzePerformanceChartWrapper
                            selectedUnit={selectedUnit}
                            subTitle={'of these foods & flavors'}
                            toolLocation={'Specific Foods & Flavors'}
                        >
                            <AnalyzePerformanceHeaderBar
                                title={"Food & Flavors"}
                                mainMetrics={mainMetrics}
                                selectedUnit={selectedUnit}
                                selectedPeriod={selectedPeriod}
                                selectedMetricId={selectedMetricId}
                                setSelectedMetricId={setSelectedMetricId}
                                setSelectedPeriod={setSelectedPeriod}
                            />
                            <AnalyzePerformanceChart
                                className="section"
                                isLoading={isLoading}
                                isPptLoading={pptReport.isLoading}
                                loadPptReport={pptReport.load}
                                data={data}
                                chartData={chartData}
                                removeChartItem={removeChartItem}
                                selectedUnit={selectedUnit}
                                selectedPeriod={selectedPeriod}
                                selectedMetricId={selectedMetricId}
                                title={'No foods & flavors selected'}
                            />
                        </AnalyzePerformanceChartWrapper>
                    )
                }
                {
                    !isLoading && data !== null && (selectedUnit === UnitType.Count || activeTab === LineTab.HistoricTrend) && (
                        <FoodsFlavorsTimeframesTable
                            data={data}
                            isLoading={isLoading}
                            selectedPeriod={selectedPeriod}
                            selectedUnit={selectedUnit}
                            selectedMetricId={selectedMetricId}
                            chartItems={chartItems}
                            addChartItem={addChartItem}
                            removeChartItem={removeChartItem}
                        />
                    )
                }
                {
                    (selectedUnit === UnitType.Pct && activeTab === LineTab.RatingsSummary) && (
                        <FoodsFlavorsTotalTable
                            selectedMetricId={selectedMetricId}
                            selectedCategories={selectedCategories}
                            setSelectedCategories={setSelectedCategories}
                        />
                    )
                }
            </AnalyzePerformanceWrapper>
            <FiltersPopup/>
            {
                noAccess ? (
                    <Footer/>
                ) : (
                    <ActionsBar>
                        <FiltersButton id="actions-bar-filters-popup-open"/>
                        {
                            selectedUnit === UnitType.Pct && (
                                <AnalyzePerformanceFooterAction
                                    isLoading={report.isLoading}
                                    loadReport={report.load}
                                />
                            )
                        }
                        <div/>
                    </ActionsBar>
                )
            }
        </>
    );
};

const FoodsFlavors: FC = () => {
    const {isLoaded} = useFiltersContext();

    if (!isLoaded) return null;

    return <FoodsFlavorsContent/>;
};

export default FoodsFlavors;